// Kept dependency-free so the axios clients can import it without pulling in the utils barrel.

export const RETURN_TO_PARAM = "returnTo";

const SIGN_IN_PATH = "/sign-in";
const STASH_KEY = "sc:returnTo";
const STASH_TTL_MS = 10 * 60 * 1000;

// Placeholder origin so parsing works the same on the server and in the browser.
const BASE = "https://socialcoach.invalid";

// Only allow same-origin paths to avoid open redirects. Parsing with URL (rather than prefix checks) catches
// tricks like "/\t/evil.com" that browsers normalize to "//evil.com".
export const sanitizeReturnTo = (value?: string | null): string | undefined => {
  if (!value || !value.startsWith("/")) {
    return undefined;
  }
  try {
    const url = new URL(value, BASE);
    if (url.origin !== BASE) {
      return undefined;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
};

export const buildSignInUrl = (returnTo?: string, extraParams: Record<string, string> = {}): string => {
  const params = new URLSearchParams(extraParams);
  const safeReturnTo = sanitizeReturnTo(returnTo);
  if (safeReturnTo) {
    params.set(RETURN_TO_PARAM, safeReturnTo);
  }
  const query = params.toString();
  return query ? `${SIGN_IN_PATH}?${query}` : SIGN_IN_PATH;
};

export const getReturnToFromSearch = (search: string): string | undefined =>
  sanitizeReturnTo(new URLSearchParams(search).get(RETURN_TO_PARAM));

// Builds a search string that carries returnTo over to another auth page (SSO, forgot password, back to login).
export const returnToSearch = (search: string): string | undefined => {
  const returnTo = getReturnToFromSearch(search);
  return returnTo ? `?${new URLSearchParams({ [RETURN_TO_PARAM]: returnTo }).toString()}` : undefined;
};

// SSO round-trips through the identity provider and bedrock lands back on "/", dropping our query params,
// so the return path is stashed in sessionStorage (per-tab, short-lived) for that flow only.
export const stashReturnTo = (returnTo?: string): void => {
  const safeReturnTo = sanitizeReturnTo(returnTo);
  if (!safeReturnTo) return;
  try {
    sessionStorage.setItem(STASH_KEY, JSON.stringify({ path: safeReturnTo, expiresAt: Date.now() + STASH_TTL_MS }));
  } catch {
    // Storage can be unavailable (private mode, blocked site data); fall back to the default landing page
  }
};

export const peekStashedReturnTo = (): string | undefined => {
  try {
    const raw = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(STASH_KEY) : null;
    if (!raw) return undefined;
    const { path, expiresAt } = JSON.parse(raw);
    if (typeof expiresAt !== "number" || expiresAt < Date.now()) return undefined;
    return sanitizeReturnTo(path);
  } catch {
    return undefined;
  }
};

export const clearStashedReturnTo = (): void => {
  try {
    sessionStorage.removeItem(STASH_KEY);
  } catch {
    // ignore
  }
};
